import { useEffect, useRef, useState } from 'react';

const ANCHO = { computadora: 1280, telefono: 390 } as const;
type Dispositivo = keyof typeof ANCHO;

/**
 * Vista previa en vivo de la tienda (arquitectura 2.19.2, I4): su propio subdominio con
 * `?vista=<plantilla>`, a escala, en computadora o teléfono. Muestra lo guardado; al guardar o
 * cambiar de plantilla se recarga.
 */
export function VistaPrevia({
  dominio,
  plantilla,
  nombre,
  version,
}: {
  dominio: string | undefined;
  plantilla: string;
  nombre: string;
  /** Cambia al guardar: recarga la vista. */
  version: number;
}) {
  const [dispositivo, setDispositivo] = useState<Dispositivo>('computadora');
  const lienzo = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(400);
  useEffect(() => {
    const el = lienzo.current;
    if (!el) return;
    const medir = new ResizeObserver(([e]) => e && setAncho(e.contentRect.width));
    medir.observe(el);
    return () => medir.disconnect();
  }, []);
  if (!dominio)
    return (
      <p className="subtitulo">
        La vista previa aparece cuando tu tienda tiene subdominio. Revisa Mi dominio.
      </p>
    );
  const anchoMarco = ANCHO[dispositivo];
  const escala = Math.min(
    1,
    (dispositivo === 'telefono' ? Math.min(ancho, 360) : ancho) / anchoMarco,
  );
  const alto = dispositivo === 'telefono' ? 760 : 820;
  const url = `https://${dominio}/?vista=${encodeURIComponent(plantilla)}&v=${version}`;
  return (
    <div className="vista-previa">
      <div className="vista-previa-barra">
        <strong>Vista previa: {nombre}</strong>
        <div className="segmentado" role="group" aria-label="Ver en">
          {(['computadora', 'telefono'] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`boton${dispositivo === d ? ' primario' : ''}`}
              aria-pressed={dispositivo === d}
              onClick={() => setDispositivo(d)}
            >
              {d === 'computadora' ? 'Computadora' : 'Teléfono'}
            </button>
          ))}
        </div>
        <a className="boton" href={url} target="_blank" rel="noopener noreferrer">
          Abrir completa
        </a>
      </div>
      <div ref={lienzo} className="vista-previa-lienzo">
        {/* La caja mide lo ya escalado: así el iframe no se corre ni ensancha la página. */}
        <div
          className="vista-previa-marco"
          style={{
            width: anchoMarco * escala,
            height: alto * escala,
            borderRadius: dispositivo === 'telefono' ? 24 : 8,
          }}
        >
          <iframe
            key={url + dispositivo}
            title={`Vista previa de tu tienda con ${nombre}`}
            src={url}
            loading="lazy"
            style={{
              width: anchoMarco,
              height: alto,
              transform: `scale(${escala})`,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>
    </div>
  );
}
